import type { ComponentType, JSX, SVGProps } from 'react';
import { motion } from 'framer-motion';
import { revealUp, revealItem, staggerParent, inViewOnce, useRevealGate } from './anim';
import { SectionGlow } from './SectionGlow';
import { IconReputation, IconGuild, IconParty } from './icons';

/**
 * Belonging — the section that finally PAYS OFF the "Social Workspace MMORPG" promise. The rest of the page
 * sells the space (voice, rooms, share); this one explains the SOUL: why a company here is a guild you grow
 * inside, and why that's belonging — never monitoring.
 *
 * EDITORIAL GUARDRAIL (the product's #1 anti-pattern): every line is framed as recognition + shared joy.
 * Reputation is peer-GIVEN and un-farmable; guild influence rises from everyone's good moments; releases are
 * celebrations. The closing bar is an explicit anti-surveillance pledge so a buyer's "is this spyware?"
 * fear is answered head-on, in the product's own warm voice.
 *
 * Placed AFTER VsZoom and BEFORE the FAQ so the emotional arc lands: "a space, not a call" → "a world you
 * belong to" → "questions". Pure cards/CSS/SVG — no Pixi — so it stays cheap and reveals like every other
 * section. All motion is gated behind reduced-motion (only transform/opacity/filter animate).
 */
const PILLARS: { Icon: ComponentType<SVGProps<SVGSVGElement>>; title: string; line: string }[] = [
  {
    Icon: IconReputation,
    title: 'Reputation, given by peers',
    line: 'Hand teammates recognition for real help. It can’t be bought or farmed — trust is the most valuable thing here.',
  },
  {
    Icon: IconGuild,
    title: 'Your company is a guild',
    line: 'Everyone’s good moments raise your guild — your whole team levels up and unlocks celebrations together.',
  },
  {
    Icon: IconParty,
    title: 'Every release is a party',
    line: 'Ship something and the office reacts: confetti, high-fives, a moment the whole guild feels.',
  },
];

export function BelongingSection(): JSX.Element {
  const reduce = useRevealGate();
  return (
    <section id="world" aria-labelledby="world-heading" className="relative mx-auto max-w-6xl px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
      <SectionGlow tone="warm" side="center" />
      <motion.div
        variants={reduce ? undefined : revealUp}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mx-auto max-w-2xl text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-300/80">More than a workspace</p>
        <h2 id="world-heading" className="mt-4 text-balance text-3xl font-bold tracking-tight text-white sm:text-5xl">
          Not a tool. A place you belong.
        </h2>
        <p className="mt-4 text-lg text-white/60">
          It’s the world’s first social workspace MMORPG: your company is a guild with its own office and
          traditions, where the people you work with become the reason you show up.
        </p>
      </motion.div>

      <motion.ul
        variants={reduce ? undefined : staggerParent}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3"
      >
        {PILLARS.map((p) => (
          <motion.li
            key={p.title}
            variants={reduce ? undefined : revealItem}
            className="group rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.015] p-7 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-amber-300/30 hover:from-white/[0.09] hover:shadow-[0_20px_50px_-20px_rgba(255,210,63,0.3)]"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-300/10 text-amber-300 ring-1 ring-amber-300/20 transition group-hover:scale-110">
              <p.Icon className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-white">{p.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{p.line}</p>
          </motion.li>
        ))}
      </motion.ul>

      {/* The pledge — a slim amber-tinted bar that doubles as the anti-surveillance promise. Visually
          distinct from the pillars so it reads as a stated commitment, not just another card. */}
      <motion.p
        variants={reduce ? undefined : revealUp}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mx-auto mt-10 max-w-3xl text-balance rounded-2xl border border-amber-300/25 bg-gradient-to-b from-amber-300/[0.08] to-transparent px-6 py-5 text-center text-base font-semibold text-amber-100 shadow-[0_20px_60px_-30px_rgba(255,210,63,0.35)] sm:text-lg"
      >
        No scores. No time-tracking. No surveillance. Ever. Just a place people actually want to be.
      </motion.p>
    </section>
  );
}
